import React from 'react';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { NotificationComposer } from 'src/app/workflows/NotificationComposer/NotificationComposer';
import { NotificationHistory } from 'src/app/workflows/NotificationHistory';
import { UserDto } from 'src/api/FiretrolApiClient';
import { useNotificationComposer } from 'src/hooks/useNotificationComposer';

export interface INotificationsTabProps {
  /** Resource type registered on the NotificationsController (e.g. "change", "transmittal"). */
  resourceType: string;
  jobId: number;
  entityId: string;
  jobUsers: UserDto[];
  customerNumber: string;
  /** Singular noun for the record used in the helper/empty-state copy (e.g. "change"). */
  entityNoun: string;
  /** Label for the attach-PDF switch in the composer. */
  attachPdfLabel: string;
}

/** @registry Notifications tab for a workflow record: NotificationHistory plus an ad-hoc composer, parameterized by resourceType. */
export const NotificationsTab: React.FC<INotificationsTabProps> = ({
  resourceType,
  jobId,
  entityId,
  jobUsers,
  customerNumber,
  entityNoun,
  attachPdfLabel,
}) => {
  const { preview, submitting, loadPreview, reloadPreview, send, skip } =
    useNotificationComposer(resourceType, jobId, entityId);
  const [composerOpen, setComposerOpen] = React.useState(false);

  const openComposer = async () => {
    await loadPreview();
    setComposerOpen(true);
  };

  const handleSend = async (
    request: Parameters<typeof send>[0],
  ): Promise<void> => {
    const result = await send(request).catch(() => null);
    if (result) setComposerOpen(false);
  };

  const handleSkip = async (
    request: Parameters<typeof skip>[0],
  ): Promise<void> => {
    const result = await skip(request).catch(() => null);
    if (result) setComposerOpen(false);
  };

  return (
    <Stack mt="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed">
          {`Sends an email for this ${entityNoun}. It does not change the status; use the workflow buttons to advance the ${entityNoun}.`}
        </Text>
        <BasicButton onClick={openComposer}>Send ad-hoc email</BasicButton>
      </Group>

      <NotificationHistory
        resourceType={resourceType}
        jobId={jobId}
        entityId={entityId}
        entityNoun={entityNoun}
      />

      <NotificationComposer
        opened={composerOpen}
        onClose={() => setComposerOpen(false)}
        preview={preview}
        jobUsers={jobUsers}
        customerNumber={customerNumber}
        submitting={submitting}
        title="Ad-hoc email"
        attachPdfLabel={attachPdfLabel}
        onSend={handleSend}
        onSkip={handleSkip}
        onReloadPreview={reloadPreview}
      />
    </Stack>
  );
};
